import React, {useEffect, useState} from "react"
import {green, amber} from "@mui/material/colors"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"
import InfoIcon from "@mui/icons-material/Info"
import CloseIcon from "@mui/icons-material/Close"
import WarningIcon from "@mui/icons-material/Warning"
import {makeStyles} from "tss-react/mui"
import PropTypes from "prop-types"
import {Snackbar, SnackbarContent, IconButton} from "@mui/material"
import clsx from "clsx"

const variantIcon = {
	success: CheckCircleIcon,
	warning: WarningIcon,
	error: ErrorIcon,
	info: InfoIcon,
}

const useSnackWrapperStyles = makeStyles()((theme) => ({
	success: {
		backgroundColor: green[600],
	},
	error: {
		backgroundColor: theme.palette.error.dark,
	},
	info: {
		backgroundColor: theme.palette.primary.dark,
	},
	warning: {
		backgroundColor: amber[700],
	},
	icon: {
		fontSize: 20,
	},
	iconVariant: {
		opacity: 0.9,
		marginRight: theme.spacing(1),
	},
	message: {
		display: "flex",
		alignItems: "center",
	},
	linkColor: {
		color: "#FFFFFF",
	},
}))

interface AlertContentProps {
	className?: string
	message?: string
	onClose?: () => void
	variant: keyof typeof variantIcon
	link?: string
}

function AlertContent(props: AlertContentProps) {
	const {classes} = useSnackWrapperStyles()
	const {className, message, onClose, variant, link, ...other} = props
	const Icon = variantIcon[variant]

	const renderLink = link !== null && link !== "" && link !== undefined ? true : false

	return (
		<SnackbarContent
			className={clsx(classes[variant], className)}
			aria-describedby="client-snackbar"
			message={
				<span id="client-snackbar" className={classes.message}>
					<Icon className={clsx(classes.icon, classes.iconVariant)} />
					{!renderLink && message}
					{renderLink && (
						<a className={clsx(classes.linkColor)} href={link}>
							{message}
						</a>
					)}
				</span>
			}
			action={[
				<IconButton key="close" aria-label="Close" color="inherit" onClick={onClose}>
					<CloseIcon className={classes.icon} />
				</IconButton>,
			]}
			{...other}
		/>
	)
}

AlertContent.propTypes = {
	className: PropTypes.string,
	message: PropTypes.node,
	onClose: PropTypes.func,
	variant: PropTypes.oneOf(["success", "warning", "error", "info"]).isRequired,
}

// const useSnackbarStyles = makeStyles((theme: Theme) => ({
//     margin: {
//         margin: theme.spacing(1),
//     }
// }));

interface AlertWithStatusProps {
	open: boolean
	success?: boolean
	message?: string
	status?: string
	closeAlert: () => void
	link?: string
	persist?: boolean
}

function AlertWithStatus(props: AlertWithStatusProps) {
	// const classes = useSnackbarStyles()
	// const [open, setOpen] = React.useState(false);

	// function handleClick(){
	//     setOpen(true);
	// }

	function handleClose(event?: React.SyntheticEvent<any, Event> | Event, reason?: string) {
		if (reason === "clickaway") {
			return
		}
		props.closeAlert()
	}

	const [varient, setVarient] = useState<string | null>(null)

	useEffect(() => {
		if (props.status) {
			const status = props.status
			if (status === "err") {
				setVarient("error")
			} else if (status === "scs") {
				setVarient("success")
			} else if (status === "wrn") {
				setVarient("warning")
			} else if (status === "inf") {
				setVarient("info")
			} else if (props.success === null) {
				setVarient("error")
			}
		}
	}, [props])

	return (
		<>
			<Snackbar
				anchorOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				open={props.open}
				autoHideDuration={props.persist ? undefined : 6000}
				onClose={handleClose}
			>
				<div>
					{varient === "success" ||
					varient === "warning" ||
					varient === "error" ||
					varient === "info" ? (
						<AlertContent
							onClose={handleClose}
							variant={varient}
							message={props.message}
							link={props.link}
						/>
					) : (
						<AlertContent
							onClose={handleClose}
							variant={props.success ? "success" : "error"}
							message={props.message}
						/>
					)}
				</div>
			</Snackbar>
		</>
	)
}

export default AlertWithStatus
